import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { Form } from '@crisellevaldez/speculo-ui';
import { MessageSquare, Info } from 'lucide-react';
import { BrandingFormData } from '../../../../../schemas/branding.schema';
import type { ConsentStyle } from '../../../../../api/storefront';
import { FormattedTextarea } from '../../../../common/FormattedTextarea';
import { IconPill } from '../../../../common/IconPill';

// The three consent treatments an org's IDX sign-up surfaces can render. An org with no
// style saved gets Required on the IDX site (search-experience resolveConsentStyle), so
// Required shows as selected until another style is chosen.
const DEFAULT_CONSENT_STYLE: ConsentStyle = 'bundled';

const CONSENT_STYLE_OPTIONS: ReadonlyArray<{
  value: ConsentStyle;
  label: string;
  description: string;
}> = [
  {
    value: 'bundled',
    label: 'Required checkbox',
    description:
      'One checkbox covering call, email and text. Visitors must tick it before they can register.',
  },
  {
    value: 'passive',
    label: 'No checkbox',
    description:
      'Passive "by submitting this form" consent, with nothing to tick and nothing blocking registration.',
  },
  {
    value: 'split',
    label: 'Split checkboxes',
    description:
      'Two optional checkboxes — one for text, one for calls and email. Neither blocks registration.',
  },
];

interface SqueezePageSectionProps {
  control: Control<BrandingFormData>;
}

export const SqueezePageSection: React.FC<SqueezePageSectionProps> = ({ control }) => {
  return (
    <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
      <div className="flex items-center gap-3 mb-3">
        <IconPill icon={MessageSquare} />
        <h4 className="text-sm font-bold text-foreground">Squeeze Page Settings</h4>
      </div>

      <hr className="dark:border-white/10" />

      <div className="mt-5">
        <Form.Control>
          <Form.Label>Consent Checkbox Style</Form.Label>
          <Controller
            name="squeezePage.consentStyle"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                {CONSENT_STYLE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-start gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name={field.name}
                      className="mt-1 shrink-0"
                      checked={(field.value || DEFAULT_CONSENT_STYLE) === option.value}
                      onChange={() => field.onChange(option.value)}
                      onBlur={field.onBlur}
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-800 dark:text-gray-200">
                        {option.label}
                      </span>
                      <span className="block text-sm text-gray-500 dark:text-gray-400">
                        {option.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          />
        </Form.Control>
      </div>

      <div className="mt-5">
        <Form.Control>
          <Form.Label>Custom Opt-In Language</Form.Label>
          <Controller
            name="squeezePage.language"
            control={control}
            render={({ field }) => (
              <FormattedTextarea
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Enter custom opt-in language..."
                rows={4}
              />
            )}
          />
          <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700/30">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">Autopopulate Tags:</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <div>
                    <code className="bg-blue-100 dark:bg-blue-800/40 px-1 rounded">{'{yyyy}'}</code> - Current year
                  </div>
                  <div>
                    <code className="bg-blue-100 dark:bg-blue-800/40 px-1 rounded">{'{mm/dd/yyyy}'}</code> - Date
                  </div>
                  <div>
                    <code className="bg-blue-100 dark:bg-blue-800/40 px-1 rounded">{'{privacy_link}'}</code> - Privacy
                    policy link
                  </div>
                  <div>
                    <code className="bg-blue-100 dark:bg-blue-800/40 px-1 rounded">{'{terms_of_use_link}'}</code> -
                    Terms link
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form.Control>
      </div>
    </div>
  );
};
